import { supabase } from '@/integrations/supabase/client';

export interface ClientAcademyProgress {
  id: string;
  client_id: string;
  academy_id: string;
  academy_name: string;
  completed_modules: number;
  total_modules: number;
}

export interface Client {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  plan: string;
  plan_price: number;
  amount_paid?: number | null;
  status: 'active' | 'expiring' | 'expired' | 'inactive';
  paid: boolean;
  has_access?: boolean;
  subscription_start: string;
  subscription_end: string;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  academy_progress?: ClientAcademyProgress[];
  // Application fields
  alias?: string | null;
  country?: string | null;
  motivation_letter?: string | null;
  applied_at?: string | null;
  // Billing fields
  billing_name?: string | null;
  billing_address?: string | null;
}

export const fetchClients = async (): Promise<Client[]> => {
  const { data: clients, error } = await supabase
    .from('clients')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }

  // Fetch academy progress for each client
  const clientsWithProgress = await Promise.all(
    (clients || []).map(async (client) => {
      const { data: progress } = await supabase
        .from('client_academy_progress')
        .select('*')
        .eq('client_id', client.id);

      return {
        ...client,
        academy_progress: progress || [],
      } as Client;
    })
  );

  return clientsWithProgress;
};

export const createClient = async (
  clientData: Omit<Client, 'id' | 'created_at' | 'updated_at' | 'academy_progress'>
): Promise<Client> => {
  const { data, error } = await supabase
    .from('clients')
    .insert(clientData)
    .select()
    .single();

  if (error) {
    console.error('Error creating client:', error);
    throw error;
  }

  // Create default academy progress for the new client
  const academies = [
    { academy_id: 'faceless-content', academy_name: 'Faceless Content Systems' },
    { academy_id: 'phantom-websites', academy_name: 'Phantom Websites & Blogs' },
    { academy_id: 'invisible-ecom', academy_name: 'Invisible E-Com' },
    { academy_id: 'ghost-newsletters', academy_name: 'Ghost Newsletters' },
    { academy_id: 'automated-business', academy_name: 'Automation Empire' },
  ];

  const progressInserts = academies.map((academy) => ({
    client_id: data.id,
    academy_id: academy.academy_id,
    academy_name: academy.academy_name,
    completed_modules: 0,
    total_modules: 10,
  }));

  await supabase.from('client_academy_progress').insert(progressInserts);

  return data as Client;
};

export const updateClient = async (
  id: string,
  updates: Partial<Client>
): Promise<Client> => {
  const { data, error } = await supabase
    .from('clients')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating client:', error);
    throw error;
  }

  return data as Client;
};

export const deleteClient = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('clients')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

export const updateClientProgress = async (
  clientId: string,
  academyId: string,
  completedModules: number
): Promise<void> => {
  const { error } = await supabase
    .from('client_academy_progress')
    .update({ completed_modules: completedModules })
    .eq('client_id', clientId)
    .eq('academy_id', academyId);

  if (error) {
    console.error('Error updating client progress:', error);
    throw error;
  }
};

// Helper functions

// Calculate status dynamically from subscription dates and paid status
// If not paid, always return 'expired' (inactive)
export const getClientStatus = (subscriptionEnd: string, paid: boolean = true): 'active' | 'expiring' | 'expired' => {
  // If not paid, client is inactive (expired)
  if (!paid) return 'expired';
  
  const days = getDaysUntilExpiration(subscriptionEnd);
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'active';
};

export const calculateClientProgress = (client: Client): number => {
  if (!client.academy_progress || client.academy_progress.length === 0) return 0;
  
  const totalModules = client.academy_progress.reduce((acc, a) => acc + a.total_modules, 0);
  const completedModules = client.academy_progress.reduce((acc, a) => acc + a.completed_modules, 0);
  
  if (totalModules === 0) return 0;
  return Math.round((completedModules / totalModules) * 100);
};

export const getActiveClients = (clients: Client[]) => 
  clients.filter(c => getClientStatus(c.subscription_end, c.paid) === 'active');

export const getExpiringClients = (clients: Client[]) => 
  clients.filter(c => getClientStatus(c.subscription_end, c.paid) === 'expiring');

export const getExpiredClients = (clients: Client[]) => 
  clients.filter(c => getClientStatus(c.subscription_end, c.paid) === 'expired');

export const getPaidClients = (clients: Client[]) => 
  clients.filter(c => c.paid);

export const getUnpaidClients = (clients: Client[]) => 
  clients.filter(c => !c.paid);

// NEW CATEGORIZATION FUNCTIONS

// Pending: Never paid (paid = false) - don't rely on status field
export const getPendingClients = (clients: Client[]) => 
  clients.filter(c => !c.paid);

// Pending Lifetime: Applied for lifetime but never paid
export const getPendingLifetimeClients = (clients: Client[]) => 
  clients.filter(c => !c.paid && c.plan === 'Lifetime');

// Pending Monthly: Applied for monthly but never paid
export const getPendingMonthlyClients = (clients: Client[]) => 
  clients.filter(c => !c.paid && c.plan === 'Monthly');

// Expired Monthly: Paid monthly but subscription lapsed (didn't renew)
export const getExpiredMonthlyClients = (clients: Client[]) => 
  clients.filter(c => c.paid && c.plan === 'Monthly' && getDaysUntilExpiration(c.subscription_end) < 0);

// Active Lifetime: Paid lifetime clients (never expires in practice)
export const getActiveLifetimeClients = (clients: Client[]) => 
  clients.filter(c => c.paid && c.plan === 'Lifetime' && c.status !== 'inactive');

// Active Monthly: Paid monthly with active subscription
export const getActiveMonthlyClients = (clients: Client[]) => 
  clients.filter(c => c.paid && c.plan === 'Monthly' && getDaysUntilExpiration(c.subscription_end) >= 0);

// Get plan description based on plan type
export const getPlanDescription = (plan: string): string => {
  switch (plan) {
    case 'Lifetime':
      return 'One-time payment • Full access forever • All 50 modules unlocked immediately';
    case 'Monthly':
      return 'Recurring subscription • $14.99/month • 5 modules/month unlocked progressively';
    default:
      return 'Custom plan';
  }
};

export const getActiveRate = (clients: Client[]) => {
  if (clients.length === 0) return 0;
  return Math.round((getActiveClients(clients).length / clients.length) * 100);
};

export const getAverageProgress = (clients: Client[]) => {
  if (clients.length === 0) return 0;
  return Math.round(clients.reduce((acc, c) => acc + calculateClientProgress(c), 0) / clients.length);
};

// Get all payments made in the current month (both monthly and lifetime/annual)
export const getMonthlyRevenue = (clients: Client[]) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  return clients.reduce((acc, c) => {
    const subscriptionDate = new Date(c.subscription_start);
    if (subscriptionDate.getMonth() === currentMonth && subscriptionDate.getFullYear() === currentYear) {
      return acc + (c.amount_paid || 0);
    }
    return acc;
  }, 0);
};

// Get all payments received from Jan 1 of current year to today
export const getYearlyRevenue = (clients: Client[]) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const startOfYear = new Date(currentYear, 0, 1);
  
  return clients.reduce((acc, c) => {
    const subscriptionDate = new Date(c.subscription_start);
    if (subscriptionDate >= startOfYear && subscriptionDate <= now) {
      return acc + (c.amount_paid || 0);
    }
    return acc;
  }, 0);
};

export const formatLastActive = (dateStr: string | null): string => {
  if (!dateStr) return 'Never';
  
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
};

export const getDaysUntilExpiration = (endDate: string): number => {
  const end = new Date(endDate);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

// Academy access logic
// Annual plan: All academies unlocked immediately
// Monthly plan: 1 academy per month OR if previous academy completed 1 week ago
export const getUnlockedAcademiesCount = (
  plan: string,
  subscriptionStart: string,
  academyProgress?: ClientAcademyProgress[]
): number => {
  // Annual plan = all 5 academies unlocked
  if (plan === 'Annual') {
    return 5;
  }

  // Monthly plan: base = 1 academy per month since subscription start
  const start = new Date(subscriptionStart);
  const now = new Date();
  const monthsSinceStart = Math.floor(
    (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30)
  );
  
  let baseUnlocked = Math.min(monthsSinceStart + 1, 5); // +1 because first academy unlocks immediately

  // Check if user can unlock extra via early completion (completed 1 week ago)
  if (academyProgress && academyProgress.length > 0) {
    const completedAcademies = academyProgress.filter(
      (a) => a.completed_modules === a.total_modules
    );
    
    // For each completed academy, if they could have unlocked the next one via early completion
    // (This is a simplified logic - in real app you'd track completion dates)
    if (completedAcademies.length >= baseUnlocked && baseUnlocked < 5) {
      baseUnlocked = Math.min(completedAcademies.length + 1, 5);
    }
  }

  return baseUnlocked;
};

export const isAcademyUnlocked = (
  academyIndex: number,
  plan: string,
  subscriptionStart: string,
  academyProgress?: ClientAcademyProgress[]
): boolean => {
  const unlockedCount = getUnlockedAcademiesCount(plan, subscriptionStart, academyProgress);
  return academyIndex < unlockedCount;
};
